import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Clock, NotePencil, X, Plus, Trash, Globe } from '@phosphor-icons/react';

const TIMEZONES = [
  { label: 'EST', tz: 'America/New_York', flag: '🇺🇸' },
  { label: 'CST', tz: 'America/Chicago', flag: '🇺🇸' },
  { label: 'MST', tz: 'America/Denver', flag: '🇺🇸' },
  { label: 'PST', tz: 'America/Los_Angeles', flag: '🇺🇸' },
  { label: 'IST', tz: 'Asia/Kolkata', flag: '🇮🇳' },
];

const formatTime = (tz) => {
  try {
    return new Date().toLocaleTimeString('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  } catch { return '--:--'; }
};
const formatDate = (tz) => {
  try {
    return new Date().toLocaleDateString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return ''; }
};

export const TimeZoneNotesWidget = ({ userName }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('time'); // 'time' | 'notes'
  const [times, setTimes] = useState({});
  const [notes, setNotes] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tz_notes') || '[]'); } catch { return []; }
  });
  const [newNote, setNewNote] = useState('');
  const panelRef = useRef(null);

  useEffect(() => {
    const update = () => {
      const t = {};
      TIMEZONES.forEach(z => { t[z.label] = { time: formatTime(z.tz), date: formatDate(z.tz) }; });
      setTimes(t);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem('tz_notes', JSON.stringify(notes));
  }, [notes]);

  const addNote = () => {
    if (!newNote.trim()) return;
    const note = { id: Date.now(), text: newNote.trim(), timestamp: new Date().toISOString(), author: userName || 'User' };
    setNotes(prev => [note, ...prev]);
    setNewNote('');
  };

  const deleteNote = (id) => setNotes(prev => prev.filter(n => n.id !== id));

  const istTime = times['IST']?.time || '--:--';

  return (
    <>
      {/* Floating trigger button */}
      <button onClick={() => setOpen(!open)}
        className="fixed bottom-5 left-5 z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 transition-all text-xs font-medium"
        data-testid="tz-widget-btn">
        <Clock size={16} weight="bold" />
        <span className="hidden sm:inline">{istTime}</span>
        <span className="text-[10px] text-slate-400">IST</span>
      </button>

      {/* Floating panel */}
      {open && (
        <div ref={panelRef} className="fixed bottom-16 left-5 z-50 w-80 max-h-[70vh] overflow-hidden rounded-xl shadow-2xl border border-slate-200 bg-white animate-in slide-in-from-bottom-4"
          data-testid="tz-widget-panel">
          {/* Header */}
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-blue-400" />
              <span className="font-semibold text-sm font-['Outfit']">Time & Notes</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X size={16} /></button>
          </div>

          {/* Tab switcher */}
          <div className="flex border-b border-slate-200">
            <button onClick={() => setTab('time')}
              className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${tab === 'time' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="tz-tab-time">
              <Clock size={14} className="inline mr-1" /> Time Zones
            </button>
            <button onClick={() => setTab('notes')}
              className={`flex-1 px-3 py-2 text-xs font-semibold transition-colors ${tab === 'notes' ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid="tz-tab-notes">
              <NotePencil size={14} className="inline mr-1" /> Notes ({notes.length})
            </button>
          </div>

          {/* Content */}
          <div className="max-h-[50vh] overflow-y-auto">
            {tab === 'time' && (
              <div className="p-3 space-y-1">
                {/* IST highlighted */}
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🇮🇳</span>
                      <div>
                        <p className="font-bold text-amber-900 text-sm">IST</p>
                        <p className="text-[10px] text-amber-600">{times['IST']?.date}</p>
                      </div>
                    </div>
                    <p className="text-xl font-bold text-amber-900 font-mono">{times['IST']?.time}</p>
                  </div>
                </div>

                <p className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider px-1 mb-1">US Time Zones</p>
                {TIMEZONES.filter(z => z.label !== 'IST').map(z => (
                  <div key={z.label} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{z.flag}</span>
                      <div>
                        <p className="font-semibold text-slate-800 text-xs">{z.label}</p>
                        <p className="text-[10px] text-slate-400">{times[z.label]?.date}</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-slate-700 font-mono">{times[z.label]?.time}</p>
                  </div>
                ))}
              </div>
            )}

            {tab === 'notes' && (
              <div className="p-3 space-y-3">
                {/* Add note */}
                <div className="space-y-2">
                  <Textarea placeholder="Write a note..." value={newNote} onChange={e => setNewNote(e.target.value)}
                    className="text-xs resize-none h-16" onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                    data-testid="tz-note-input" />
                  <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700 text-xs gap-1" onClick={addNote} disabled={!newNote.trim()} data-testid="tz-add-note-btn">
                    <Plus size={12} weight="bold" /> Add Note
                  </Button>
                </div>

                {/* Notes list */}
                {notes.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No notes yet. Add one above.</p>}
                {notes.map(note => (
                  <div key={note.id} className="bg-slate-50 rounded-lg p-2.5 border border-slate-100 group">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs text-slate-700 flex-1 whitespace-pre-wrap">{note.text}</p>
                      <button onClick={() => deleteNote(note.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                        <Trash size={12} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge variant="outline" className="text-[9px] text-slate-400">{note.author}</Badge>
                      <span className="text-[9px] text-slate-400">{new Date(note.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
